import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { Approval } from "./types";

// The GA1 four-eyes approval cycle (the human-operable half). The console drives
// it explicitly — the recommendation Tier-2 is a DEFERRED approval (advisor
// requests now; a second person grants later), distinct from the AC1 client's
// synchronous 403→retry interceptor. All calls ride the same ApiClient.
const MINE = ["approvals", "mine"];
const AWAITING = ["approvals", "awaiting-me"];

/** The requester's own approvals — so the advisor sees "approved → ready". */
export function useMyApprovals(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: MINE,
    queryFn: () => api.get<Approval[]>("/v1/approvals/mine"),
    enabled,
  });
}

/** The approver's inbox — PENDING approvals this caller may grant (≠ requester). */
export function useAwaitingMe(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: AWAITING,
    queryFn: () => api.get<Approval[]>("/v1/approvals/awaiting-me"),
    enabled,
  });
}

function useInvalidateApprovals() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["approvals"] });
  };
}

export function useRequestApproval() {
  const api = useApi();
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: (input: { action: string; resourceId: string }) =>
      api.post<Approval>("/v1/approvals", input),
    onSettled: invalidate,
  });
}

export function useApproveApproval() {
  const api = useApi();
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: (approvalId: string) => api.post<Approval>(`/v1/approvals/${approvalId}/approve`),
    onSettled: invalidate,
  });
}

export function useRejectApproval() {
  const api = useApi();
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: ({ approvalId, reason }: { approvalId: string; reason?: string }) =>
      api.post<Approval>(`/v1/approvals/${approvalId}/reject`, { reason }),
    onSettled: invalidate,
  });
}
